import React from 'react';
import { Icon } from 'semantic-ui-react';
import CurrentElectricBill from './inputGroups/CurrentElectricBill';
import NewEnergySystem from './inputGroups/NewEnergySystem';
import AdditionalBudgetItems from './inputGroups/AdditionalBudgetItems';
import FederalItc from './inputGroups/FederalItc';
import Grants from './inputGroups/Grants';
import OperatingLoans from './inputGroups/OperatingLoans';
import ConstructionLoan from './inputGroups/ConstructionLoan';
import { Popup } from 'semantic-ui-react';

// Import all the type definitions from BudgetCalculator
type CurrentElectricBillValues = {
    usage?: number | null;
    cost?: number | null;
};

type NewEnergySystemValues = {
    newEnergySystem?: string | null;
    costOfNewEnergySystem?: number | null;
    energySystemSize?: number | null;
    energyProductionYear1?: number | null;
    expectedElectricBillSavingsYear1?: number | null;
    expectedBillEscalationRate?: number | null;
    renewableEnergyCreditsValue?: number | null;
    operationMaintenanceCosts?: number | null;
    includeInverterReplacement?: boolean | null;
};

type AdditionalBudgetItemsValues = {
    programAdministrationOverhead?: number | null;
    otherContingency?: number | null;
};

type FederalItcValues = {
    percentage?: number | null;
};

type GrantValues = {
    id: string;
    amount?: number | null;
    availableForConstruction?: boolean;
};

type GrantsValues = {
    grants: GrantValues[];
};

type OperatingLoanValues = {
    id: string;
    loanName?: string | null;
    amount?: number | null;
    rate?: number | null;
    term?: number | null;
    amortization?: number | null;
    availableForConstruction?: boolean;
};

type OperatingLoansValues = {
    loans: OperatingLoanValues[];
};

// Keep the old format for BudgetCalculator compatibility
type LegacyOperatingLoanValues = {
    loanName?: string | null;
    amount?: number | null;
    rate?: number | null;
    term?: number | null;
    amortization?: number | null;
    availableForConstruction?: boolean | null;
};

type LegacyLoansValues = {
    loan1: LegacyOperatingLoanValues;
    loan2: LegacyOperatingLoanValues;
};

type ConstructionLoanValues = {
    loanName?: string | null;
    amount?: number | null;
    totalInterestPayments?: number | null;
    hasLoan?: boolean;
};

type BudgetCalculatorState = {
    currentElectricBill: CurrentElectricBillValues;
    newEnergySystem: NewEnergySystemValues;
    additionalBudgetItems: AdditionalBudgetItemsValues;
    federalItc: FederalItcValues;
    grants: GrantsValues;
    operatingLoans: LegacyLoansValues;
    constructionLoan: ConstructionLoanValues;
};

type InternalState = {
    currentElectricBill: CurrentElectricBillValues;
    newEnergySystem: NewEnergySystemValues;
    additionalBudgetItems: AdditionalBudgetItemsValues;
    federalItc: FederalItcValues;
    grants: GrantsValues;
    operatingLoans: OperatingLoansValues;
    constructionLoan: ConstructionLoanValues;
};

type Props = {
    values: BudgetCalculatorState;
    onChange: (values: BudgetCalculatorState) => void;
    onCalculate: () => void;
};

const InputReview: React.FC<Props> = ({ values, onChange, onCalculate }) => {
    // Convert legacy format to internal format
    const convertToInternalState = (legacyState: BudgetCalculatorState): InternalState => {
        const operatingLoans: OperatingLoansValues = {
            loans: []
        };
        
        // Convert loan1 if it has data
        if (legacyState.operatingLoans.loan1 && 
            (legacyState.operatingLoans.loan1.amount || 
             legacyState.operatingLoans.loan1.loanName ||
             legacyState.operatingLoans.loan1.rate ||
             legacyState.operatingLoans.loan1.term ||
             legacyState.operatingLoans.loan1.amortization)) {
            operatingLoans.loans.push({
                id: 'loan1',
                ...legacyState.operatingLoans.loan1,
                availableForConstruction: legacyState.operatingLoans.loan1.availableForConstruction ?? false
            });
        }
        
        // Convert loan2 if it has data
        if (legacyState.operatingLoans.loan2 && 
            (legacyState.operatingLoans.loan2.amount || 
             legacyState.operatingLoans.loan2.loanName ||
             legacyState.operatingLoans.loan2.rate ||
             legacyState.operatingLoans.loan2.term ||
             legacyState.operatingLoans.loan2.amortization)) {
            operatingLoans.loans.push({
                id: 'loan2',
                ...legacyState.operatingLoans.loan2,
                availableForConstruction: legacyState.operatingLoans.loan2.availableForConstruction ?? false
            });
        }

        return {
            ...legacyState,
            operatingLoans
        };
    };

    // Convert internal format back to legacy format
    const convertToLegacyState = (internalState: InternalState): BudgetCalculatorState => {
        const legacyOperatingLoans: LegacyLoansValues = {
            loan1: {},
            loan2: {}
        };

        // Convert loans array back to loan1/loan2 format
        internalState.operatingLoans.loans.forEach((loan, index) => {
            if (index === 0) {
                legacyOperatingLoans.loan1 = {
                    loanName: loan.loanName,
                    amount: loan.amount,
                    rate: loan.rate,
                    term: loan.term,
                    amortization: loan.amortization,
                    availableForConstruction: loan.availableForConstruction
                };
            } else if (index === 1) {
                legacyOperatingLoans.loan2 = {
                    loanName: loan.loanName,
                    amount: loan.amount,
                    rate: loan.rate,
                    term: loan.term,
                    amortization: loan.amortization,
                    availableForConstruction: loan.availableForConstruction
                };
            }
        });

        return {
            ...internalState,
            operatingLoans: legacyOperatingLoans
        };
    };

    const internalState = convertToInternalState(values);

    const handleSectionUpdate = (section: string, newValues: any) => {
        const updatedInternalState = {
            ...internalState,
            [section]: newValues,
        };
        
        // Convert back to legacy format before calling onChange
        const legacyState = convertToLegacyState(updatedInternalState);
        onChange(legacyState);
    };

    return (
        <div>
            <h2>
                Technical Inputs
                <Popup
                    content="Get this information from your contractor or technical advisor."
                    trigger={<Icon name="info circle" />}
                    position="right center"
                />
            </h2>
            {/* <CurrentElectricBill
                values={values.currentElectricBill}
                onChange={(newValues) => handleSectionUpdate('currentElectricBill', newValues)}
            /> */}
            <NewEnergySystem
                values={values.newEnergySystem}
                onChange={(newValues) => handleSectionUpdate('newEnergySystem', newValues)}
            />
            <h2>
                Financial Inputs
                <Popup
                    content="Get this information from your accountant or financial advisor."
                    trigger={<Icon name="info circle" />}
                    position="right center"
                />
            </h2>
            <AdditionalBudgetItems
                values={values.additionalBudgetItems}
                onChange={(newValues) => handleSectionUpdate('additionalBudgetItems', newValues)}
            />
            <FederalItc
                values={values.federalItc}
                onChange={(newValues) => handleSectionUpdate('federalItc', newValues)}
            />
            <ConstructionLoan
                values={internalState.constructionLoan}
                onChange={(newValues) => handleSectionUpdate('constructionLoan', newValues)}
            />

            <OperatingLoans
                values={internalState.operatingLoans}
                onChange={(newValues) => handleSectionUpdate('operatingLoans', newValues)}
            />

            <Grants
                values={internalState.grants}
                onChange={(newValues) => handleSectionUpdate('grants', newValues)}
            />
        </div>
    );
};

export default InputReview;